import React, { useEffect, useMemo, useRef, useState } from "react";
import {
  Alert,
  Button,
  Card,
  Input,
  InputNumber,
  Space,
  Table,
  Tag,
  Tooltip,
  Typography,
} from "antd";
import { errorText, scoreText } from "./appraisalUtils";
import { WarningOutlined } from "@ant-design/icons";

const { Text } = Typography;
const FORMULA_LIMIT = 2000;

const savedConfigFor = (configs, component) =>
  configs.find(
    (config) =>
      config.component === component.value && config.isActive === true,
  );

const formulaOf = (config) =>
  config?.formula ?? config?.expression ?? config?.formulaExpression ?? "";

const initialContext = () => ({});

const draftFor = (component, config) => ({
  formula: formulaOf(config),
  context: initialContext(),
  edited: false,
});

const componentIdentity = (component) => component.value;

const isFiniteInRange = (value) =>
  value !== null &&
  value !== undefined &&
  Number.isFinite(Number(value)) &&
  Number(value) >= 0 &&
  Number(value) <= 1000;

const formulaValidation = (draft, component) => {
  const formula = String(draft?.formula ?? "");
  if (!formula.trim()) return "Enter a formula.";
  if (formula.length > FORMULA_LIMIT)
    return `Formula must be ${FORMULA_LIMIT} characters or fewer.`;
  for (const [variable] of component.variableMeanings || []) {
    const value = draft?.context?.[variable];
    if (
      value !== undefined &&
      value !== null &&
      value !== "" &&
      !isFiniteInRange(value)
    )
      return `${variable} must be a number between 0 and 1000.`;
  }
  return "";
};

const responseFor = (response, component) => {
  if (Array.isArray(response?.results))
    return (
      response.results.find((item) => item.component === component) || null
    );
  if (Array.isArray(response))
    return response.find((item) => item.component === component) || null;
  return null;
};

const getFailure = (error) => {
  const payload = error?.response?.data ?? error?.data ?? error;
  return errorText(payload);
};

export default function AppraisalFormulaPreview({
  request,
  components = [],
  configs = [],
  canPreview = false,
  configurationLoading = false,
}) {
  const [drafts, setDrafts] = useState({});
  const [results, setResults] = useState({});
  const [requestError, setRequestError] = useState("");
  const [running, setRunning] = useState(false);
  const draftsRef = useRef(drafts);
  const runningRef = useRef(false);
  const mountedRef = useRef(false);
  const sequenceRef = useRef(0);
  draftsRef.current = drafts;

  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
      sequenceRef.current += 1;
    };
  }, []);

  useEffect(() => {
    const refreshed = components.filter((component) => {
      const key = componentIdentity(component);
      const existing = draftsRef.current[key];
      if (existing?.edited) return false;
      const seeded = draftFor(component, savedConfigFor(configs, component));
      return (
        existing &&
        (existing.formula !== seeded.formula ||
          JSON.stringify(existing.context) !== JSON.stringify(seeded.context))
      );
    });
    if (refreshed.length) {
      setResults((current) => {
        const next = { ...current };
        refreshed.forEach((component) => delete next[component.value]);
        return next;
      });
    }
    setDrafts((current) => {
      const next = { ...current };
      components.forEach((component) => {
        const key = componentIdentity(component);
        const config = savedConfigFor(configs, component);
        if (!next[key]) next[key] = draftFor(component, config);
        else if (!next[key].edited) next[key] = draftFor(component, config);
      });
      return next;
    });
  }, [components, configs]);

  const busy = running;
  const orderedComponents = useMemo(
    () => components.filter((item) => item.value !== "SUPERVISOR_5R"),
    [components],
  );

  const updateDraft = (component, change) => {
    const key = componentIdentity(component);
    setDrafts((current) => ({
      ...current,
      [key]: {
        ...(current[key] || draftFor(component, null)),
        ...change,
        edited: true,
      },
    }));
    setResults((current) => {
      if (!current[key]) return current;
      const next = { ...current };
      delete next[key];
      return next;
    });
  };

  const resetDraft = (component) => {
    if (!canPreview || busy) return;
    const key = componentIdentity(component);
    setDrafts((current) => ({
      ...current,
      [key]: draftFor(component, savedConfigFor(configs, component)),
    }));
    setResults((current) => {
      const next = { ...current };
      delete next[key];
      return next;
    });
    setRequestError("");
  };

  const run = async (targets) => {
    if (!canPreview || configurationLoading || busy || runningRef.current)
      return;
    if (typeof request !== "function") {
      setRequestError(
        "Formula preview is unavailable because no request client was provided.",
      );
      return;
    }
    setRequestError("");
    const snapshot = targets.map((component) => ({
      component,
      key: componentIdentity(component),
      draft: drafts[componentIdentity(component)] || draftFor(component, null),
    }));
    const invalid = snapshot.filter(({ component, draft }) =>
      formulaValidation(draft, component),
    );
    const valid = snapshot.filter(
      ({ component, draft }) => !formulaValidation(draft, component),
    );
    setResults((current) => {
      const next = { ...current };
      valid.forEach(({ key }) => delete next[key]);
      invalid.forEach(({ key, component, draft }) => {
        next[key] = {
          submittedFormula: draft.formula,
          submittedContext: draft.context,
          localError: formulaValidation(draft, component),
        };
      });
      return next;
    });
    if (!valid.length) return;
    const sequence = ++sequenceRef.current;
    runningRef.current = true;
    setRunning(true);
    setRequestError("");
    try {
      const payload = {
        items: valid.map(({ component, draft }) => {
          const context = {};
          (component.variableMeanings || []).forEach(([variable]) => {
            const value = draft.context?.[variable];
            if (value !== undefined && value !== null && value !== "")
              context[variable] = Number(value);
          });
          return {
            component: component.value,
            formula: String(draft.formula),
            context,
          };
        }),
      };
      const response = await request("POST", "configs/preview-batch", payload);
      if (!mountedRef.current || sequence !== sequenceRef.current) return;
      setResults((current) => {
        const next = { ...current };
        valid.forEach(({ key, draft, component }) => {
          const latest = draftsRef.current[key];
          if (
            !latest ||
            latest.formula !== draft.formula ||
            JSON.stringify(latest.context) !== JSON.stringify(draft.context)
          ) {
            delete next[key];
            return;
          }
          const item = responseFor(response, component.value);
          next[key] = item
            ? {
                ...item,
                submittedFormula: draft.formula,
                submittedContext: draft.context,
              }
            : {
                submittedFormula: draft.formula,
                submittedContext: draft.context,
                localError:
                  "No preview result was returned for this component.",
              };
        });
        return next;
      });
    } catch (error) {
      if (mountedRef.current && sequence === sequenceRef.current)
        setRequestError(getFailure(error));
    } finally {
      if (mountedRef.current && sequence === sequenceRef.current) {
        runningRef.current = false;
        setRunning(false);
      }
    }
  };

  const columns = [
    {
      title: "Component and formula",
      key: "formula",
      render: (_, component) => {
        const draft = drafts[component.value] || draftFor(component, null);
        const result = results[component.value];
        const formulaError = result?.formulaError || result?.localError;
        const suggestedError = result?.suggestedError;
        return (
          <Space direction="vertical" size={8} style={{ width: "100%" }}>
            <Text strong>{component.label}</Text>
            <Input.TextArea
              aria-label={`${component.label} formula`}
              value={draft.formula}
              maxLength={FORMULA_LIMIT}
              showCount
              autoSize={{ minRows: 2, maxRows: 6 }}
              disabled={!canPreview || busy}
              onChange={(event) =>
                updateDraft(component, { formula: event.target.value })
              }
            />
            {component.suggestedFormula && (
              <Text type="secondary">
                Suggested default: <Text code>{component.suggestedFormula}</Text>
              </Text>
            )}
            {formulaError && (
              <Alert type="error" showIcon message={errorText(formulaError)} />
            )}
            {suggestedError && (
              <Alert
                type="error"
                showIcon
                message={`Suggested formula: ${errorText(suggestedError)}`}
              />
            )}
            {component.variableMeanings?.map(([variable, meaning]) => (
              <div key={variable}>
                <Tooltip title={meaning}>
                  <Text>
                    <Text code>{variable}</Text>: {meaning}
                  </Text>
                </Tooltip>
                <InputNumber
                  aria-label={`${component.label} ${variable}`}
                  min={0}
                  max={1000}
                  precision={0}
                  value={draft.context?.[variable]}
                  placeholder="Optional (defaults to 0)"
                  disabled={!canPreview || busy}
                  style={{ display: "block", width: "100%", marginTop: 4 }}
                  onChange={(value) =>
                    updateDraft(component, {
                      context: { ...draft.context, [variable]: value },
                    })
                  }
                />
              </div>
            ))}
            {component.variableNote && (
              <Text type="secondary">{component.variableNote}</Text>
            )}
            <Space>
              <Button
                size="small"
                disabled={!canPreview || busy}
                onClick={() => resetDraft(component)}
              >
                Use current saved formula
              </Button>
              <Button
                size="small"
                type="primary"
                disabled={!canPreview || busy}
                onClick={() => run([component])}
              >
                Run preview
              </Button>
            </Space>
          </Space>
        );
      },
    },
    {
      title: "Preview",
      key: "preview",
      render: (_, component) => {
        const result = results[component.value];
        if (!result) return <Text type="secondary">Not calculated</Text>;
        const calculated = result.score ?? result.calculatedScore;
        const suggestedScore = result.suggestedScore;
        const delta = result.delta ?? result.scoreDelta;
        const why = result.why ?? result.explanation ?? result.reason;
        return (
          <Space direction="vertical" size={4}>
            <Text>
              Formula score:{" "}
              {scoreText(calculated).replace("Not scored", "Not calculated")}
            </Text>
            <Text>
              Suggested formula: {result.suggestedFormula || "Not calculated"}
            </Text>
            <Text>
              Suggested score:{" "}
              {scoreText(suggestedScore).replace(
                "Not scored",
                "Not calculated",
              )}
            </Text>
            <Text>
              Difference:{" "}
              {scoreText(delta).replace("Not scored", "Not calculated")}
            </Text>
            <Text>Why: {why ? errorText(why) : "Not calculated"}</Text>
            {result.clamped && (
              <Tag color="orange" icon={<WarningOutlined />}>
                Score clamped to 0–100
              </Tag>
            )}
          </Space>
        );
      },
    },
  ];

  return (
    <Card title="Formula preview" loading={configurationLoading}>
      <Typography.Paragraph type="secondary">
        Compare your formula with the suggested formula on the same numbers
        before saving. Difference is your formula score minus the suggested
        score. A saved formula is the first source of truth; the suggested
        math runs only when no formula is set. Running a preview does not
        save changes.
      </Typography.Paragraph>
      {requestError && (
        <Alert
          type="error"
          showIcon
          message={requestError}
          style={{ marginBottom: 16 }}
        />
      )}
      {!canPreview && (
        <Alert
          type="info"
          showIcon
          message="Formula previews are available to system administrators."
          style={{ marginBottom: 16 }}
        />
      )}
      <Space style={{ marginBottom: 12 }}>
        <Button
          type="primary"
          disabled={!canPreview || busy || orderedComponents.length === 0}
          onClick={() => run(orderedComponents)}
        >
          Run all previews
        </Button>
        {busy && <Text type="secondary">Preview running…</Text>}
      </Space>
      <Table
        rowKey="value"
        size="small"
        pagination={false}
        dataSource={orderedComponents}
        columns={columns}
        scroll={{ x: 900 }}
      />
    </Card>
  );
}
